import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { formatDateTime } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

interface StaffRow {
  id: string;
  email: string;
  name: string;
  role: string;
  active: boolean;
  lastLoginAt: string | null;
  locked: boolean;
}

export const ROLES: { id: string; label: string; what: string }[] = [
  { id: 'OWNER', label: 'Owner', what: 'Everything, including payroll approval and staff logins' },
  { id: 'MANAGER', label: 'Manager', what: 'Runs the hotel day to day: rooms, rates, menu, refunds, roster; sees no pay' },
  { id: 'FRONT_DESK', label: 'Front desk', what: 'Bookings, guests, payments, pool and transport, laundry, messages' },
  { id: 'ACCOUNTANT', label: 'Accountant', what: 'The books, reports, pay data and payroll (cannot approve it)' },
  { id: 'HOUSEKEEPING', label: 'Housekeeping', what: 'Cleaning tasks, faults, laundry' },
  { id: 'RESTAURANT', label: 'Restaurant', what: 'Orders, bills, payments, own till' },
  { id: 'KITCHEN', label: 'Kitchen', what: 'Kitchen and bar screens, stock deliveries and waste' },
  { id: 'ATTENDANT', label: 'Pool attendant', what: 'Pool bookings and payments, own till' },
  { id: 'DRIVER', label: 'Driver', what: 'Own trips only, never prices' },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.id, r.label]));

/** An easy-to-read temporary password: no look-alike characters. The owner reads it out; the person changes it at once. */
export function makePassword(): string {
  const letters = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const pick = (set: string) => set[crypto.getRandomValues(new Uint32Array(1))[0]! % set.length]!;
  const word = () => Array.from({ length: 4 }, () => pick(letters)).join('');
  return `${word()}-${word()}-${Array.from({ length: 3 }, () => pick(digits)).join('')}`;
}

function useStaffMutation<V, R = unknown>(run: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: run, onSuccess: () => qc.invalidateQueries({ queryKey: ['staff'] }) });
}

export function Staff() {
  const q = useQuery({ queryKey: ['staff'], queryFn: () => api<{ staff: StaffRow[] }>('/staff').then((r) => r.staff) });
  const toast = useToast();
  const report = useReport();
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState<StaffRow | null>(null);
  const [editing, setEditing] = useState<StaffRow | null>(null);
  const act = useStaffMutation((v: { id: string; what: 'unlock' | 'deactivate' | 'activate' }) => api(`/staff/${v.id}/${v.what}`, { method: 'POST', body: {} }));

  return (
    <div className="page">
      <PageHead eyebrow="Setup" title="Staff logins">
        <Button variant="primary" onClick={() => setAdding(true)}>Add a person</Button>
      </PageHead>
      <p className="small muted">Each person signs in with their own email and password. Remove a leaver's login the same day. Only the owner manages logins. Link a login to a staff record under People so the person can clock in and see their payslips.</p>
      {q.isLoading ? (
        <Loading rows={3} />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      ) : (q.data ?? []).length === 0 ? (
        <div className="card flat"><Empty icon="people" title="No logins yet">Add the first person.</Empty></div>
      ) : (
        <div className="list">
          {q.data!.map((s) => (
            <div key={s.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{s.name}</span>
                <span className="row">
                  {!s.active ? <Badge tone="bad">Removed</Badge> : null}
                  {s.locked ? <Badge tone="warn">Locked out</Badge> : null}
                  <Badge>{ROLE_LABEL[s.role] ?? s.role}</Badge>
                </span>
              </div>
              <div className="small muted">{s.email} · {s.lastLoginAt ? `last signed in ${formatDateTime(s.lastLoginAt)}` : 'has not signed in yet'}</div>
              <div className="row">
                <Button onClick={() => setResetting(s)}>Set a new password</Button>
                <Button variant="ghost" onClick={() => setEditing(s)}>Change name or role</Button>
                {s.locked ? <Button onClick={() => act.mutate({ id: s.id, what: 'unlock' }, { onError: report, onSuccess: () => toast.ok('Unlocked') })}>Unlock</Button> : null}
                {s.active ? (
                  <Button variant="ghost" onClick={() => act.mutate({ id: s.id, what: 'deactivate' }, { onError: report, onSuccess: () => toast.ok(`${s.name} can no longer sign in`) })}>Remove login</Button>
                ) : (
                  <Button onClick={() => act.mutate({ id: s.id, what: 'activate' }, { onError: report, onSuccess: () => toast.ok('Login restored') })}>Restore login</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {adding ? <AddDialog onClose={() => setAdding(false)} /> : null}
      {resetting ? <ResetDialog person={resetting} onClose={() => setResetting(null)} /> : null}
      {editing ? <EditDialog person={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

function PasswordField({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field id={id} label="Temporary password" hint="At least 10 characters. Tell the person; they should change it when they first sign in.">
      <div className="row">
        <input id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)} autoComplete="off" />
        <Button type="button" onClick={() => onChange(makePassword())}>Make one</Button>
      </div>
    </Field>
  );
}

function AddDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('FRONT_DESK');
  const [password, setPassword] = useState('');
  const make = useStaffMutation(() => api('/staff', { method: 'POST', body: { name: name.trim(), email: email.trim(), role, password } }));
  const valid = name.trim().length >= 2 && /^\S+@\S+\.\S+$/.test(email.trim()) && password.length >= 10;
  const what = ROLES.find((r) => r.id === role)?.what;
  return (
    <Modal open onClose={onClose} title="Add a person">
      <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (valid) make.mutate(undefined, { onError: report, onSuccess: () => { toast.ok(`${name.trim()} can now sign in`); onClose(); } }); }}>
        <Field id="sn" label="Full name"><input id="sn" className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
        <Field id="se" label="Email (they sign in with it)"><input id="se" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" /></Field>
        <Field id="sr" label="Role" hint={what}>
          <select id="sr" className="select" value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </Field>
        <PasswordField id="sp" value={password} onChange={setPassword} />
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>Add</Button>
        </div>
      </form>
    </Modal>
  );
}

function ResetDialog({ person, onClose }: { person: StaffRow; onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [password, setPassword] = useState('');
  const reset = useStaffMutation(() => api(`/staff/${person.id}/reset-password`, { method: 'POST', body: { password } }));
  return (
    <Modal open onClose={onClose} title={`New password for ${person.name}`}>
      <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (password.length >= 10) reset.mutate(undefined, { onError: report, onSuccess: () => { toast.ok('Password set. They are signed out everywhere.'); onClose(); } }); }}>
        <p className="small muted">This also unlocks the login and signs the person out of every phone.</p>
        <PasswordField id="rp" value={password} onChange={setPassword} />
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={password.length < 10} loading={reset.isPending}>Set password</Button>
        </div>
      </form>
    </Modal>
  );
}

function EditDialog({ person, onClose }: { person: StaffRow; onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [name, setName] = useState(person.name);
  const [role, setRole] = useState(person.role);
  const save = useStaffMutation(() => api(`/staff/${person.id}`, { method: 'PATCH', body: { ...(name.trim() !== person.name ? { name: name.trim() } : {}), ...(role !== person.role ? { role } : {}) } }));
  const changed = name.trim() !== person.name || role !== person.role;
  return (
    <Modal open onClose={onClose} title={person.name}>
      <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (changed && name.trim().length >= 2) save.mutate(undefined, { onError: report, onSuccess: () => { toast.ok('Saved'); onClose(); } }); }}>
        <Field id="en" label="Full name"><input id="en" className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field id="er" label="Role" hint={ROLES.find((r) => r.id === role)?.what}>
          <select id="er" className="select" value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!changed || name.trim().length < 2} loading={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
